import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { Job } from '../../types/backend.types';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { FullScreenMapPicker } from './FullScreenMapPicker';
import { CustomSelect } from '../common/CustomSelect';
import {
  Search, Briefcase, Plus, Edit, Trash2, Archive,
  RotateCcw, Save, X, MapPin, Map as MapIcon,
  Building, Phone, User, Calendar, AlertCircle
} from 'lucide-react';

interface JobFormData {
  job_number: string;
  bradanna_job_number: string;
  job_name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  general_contractor: string;
  contractor_id?: number;
  superintendent_name: string;
  superintendent_phone: string;
  project_manager_name: string;
  project_manager_phone: string;
  bradanna_project_manager: string;
  lat?: number;
  lng?: number;
  geofence_type: 'circle' | 'polygon';
  geofence_data: {
    radius?: number;
    points?: Array<{ lat: number; lng: number }>;
  };
  is_asphalt_project: boolean;
}

interface Contractor {
  contractor_id: number;
  contractor_name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  is_active: boolean;
}

interface ContractorFormData {
  contractor_name: string;
  contact_name: string;
  phone: string;
  email: string;
  address: string;
}


export const JobManagement: React.FC = () => {
  const auth = useContext(AuthContext);

  // Admin/Foreman access control
  if (!auth?.user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (auth.user.role !== 'admin' && auth.user.role !== 'foreman') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators and foremen.</p>
        <p>Your current role: <strong>{auth.user.role}</strong></p>
      </div>
    );
  }

  const [jobs, setJobs] = useState<Job[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'all'>('active');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [previewJobNumber, setPreviewJobNumber] = useState<string>('');

  // Contractor autocomplete
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [showContractorDropdown, setShowContractorDropdown] = useState(false);
  const [filteredContractors, setFilteredContractors] = useState<Contractor[]>([]);

  // Add contractor modal
  const [showAddContractorModal, setShowAddContractorModal] = useState(false);
  const [contractorFormData, setContractorFormData] = useState<ContractorFormData>({
    contractor_name: '',
    contact_name: '',
    phone: '',
    email: '',
    address: ''
  });

  // Error popup modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState('Error');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  // Helper to show error popup
  const showErrorPopup = (title: string, message: string, details?: string[]) => {
    setErrorTitle(title);
    setError(message);
    setErrorDetails(details || []);
    setShowErrorModal(true);
  };

  // Form data for add/edit
  const [formData, setFormData] = useState<JobFormData>({
    job_number: '',
    bradanna_job_number: '',
    job_name: '',
    address: '',
    city: '',
    state: 'FL',
    zip_code: '',
    general_contractor: '',
    superintendent_name: '',
    superintendent_phone: '',
    project_manager_name: '',
    project_manager_phone: '',
    bradanna_project_manager: '',
    geofence_type: 'circle',
    geofence_data: { radius: 200 },
    is_asphalt_project: false
  });

  // Load jobs
  useEffect(() => {
    loadJobs();
  }, [viewMode]);

  // Load contractors for autocomplete
  useEffect(() => {
    loadContractors();
  }, []);

  // Filter contractors when user types
  useEffect(() => {
    if (!formData.general_contractor) {
      setFilteredContractors([]);
      return;
    }

    const filtered = contractors.filter(contractor =>
      contractor.contractor_name.toLowerCase().includes(formData.general_contractor.toLowerCase())
    );
    setFilteredContractors(filtered);
  }, [formData.general_contractor, contractors]);

  // Fetch preview job number when modal opens or asphalt flag changes
  useEffect(() => {
    if (showAddModal && !editingJob) {
      fetchPreviewJobNumber();
    }
  }, [showAddModal, formData.is_asphalt_project]);

  // Handle Android back button for map picker
  useEffect(() => {
    const handleBackButton = (e: PopStateEvent) => {
      if (showMapPicker) {
        e.preventDefault();
        setShowMapPicker(false);
        // Push state back so the browser doesn't navigate away
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    if (showMapPicker) {
      // Push a state when map picker opens
      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handleBackButton);
    }

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [showMapPicker]);

  const fetchPreviewJobNumber = async () => {
    try {
      const response = await api.get(`${API_CONFIG.ENDPOINTS.JOBS}/preview-number?is_asphalt_project=${formData.is_asphalt_project}`);
      if (response.data.success) {
        setPreviewJobNumber(response.data.data.job_number);
      }
    } catch (err) {
      console.error('Failed to fetch preview job number:', err);
      setPreviewJobNumber('');
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = API_CONFIG.ENDPOINTS.JOBS;
      if (viewMode === 'archived') {
        url += '?is_active=false';
      } else if (viewMode === 'all') {
        url += '?include_archived=true';
      }

      const response = await api.get(url);
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadContractors = async () => {
    try {
      const response = await api.get('/api/contractors', { params: { is_active: 'true' } });
      if (response.data.success) {
        setContractors(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load contractors:', err);
    }
  };

  // Handle adding a new contractor
  const handleAddContractor = () => {
    setContractorFormData({
      contractor_name: '',
      contact_name: '',
      phone: '',
      email: '',
      address: ''
    });
    setShowAddContractorModal(true);
  };

  // Save new contractor
  const handleSaveContractor = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate contractor name
      if (!contractorFormData.contractor_name || contractorFormData.contractor_name.trim() === '') {
        setError('Contractor name is required');
        setLoading(false);
        return;
      }

      const response = await api.post('/api/contractors', contractorFormData);

      if (response.data.success) {
        // Reload contractors list
        await loadContractors();

        // Auto-select the new contractor in the job form
        const newContractor = response.data.data;
        setFormData({
          ...formData,
          general_contractor: contractorFormData.contractor_name,
          contractor_id: newContractor.contractor_id
        });

        // Close modal
        setShowAddContractorModal(false);

        // Clear form
        setContractorFormData({
          contractor_name: '',
          contact_name: '',
          phone: '',
          email: '',
          address: ''
        });
      }
    } catch (err: any) {
      console.error('Failed to save contractor:', err);
      setError(err.response?.data?.error || 'Failed to save contractor');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort jobs - prioritize matches at start of name
  const filteredJobs = jobs
    .filter(job =>
      job.job_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.bradanna_job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.general_contractor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.address?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const search = searchTerm.toLowerCase();

      // Priority 1: Job name STARTS with search term
      const aStartsWithName = a.job_name?.toLowerCase().startsWith(search);
      const bStartsWithName = b.job_name?.toLowerCase().startsWith(search);
      if (aStartsWithName && !bStartsWithName) return -1;
      if (!aStartsWithName && bStartsWithName) return 1;

      // Priority 2: Job name CONTAINS search term
      const aContainsName = a.job_name?.toLowerCase().includes(search);
      const bContainsName = b.job_name?.toLowerCase().includes(search);
      if (aContainsName && !bContainsName) return -1;
      if (!aContainsName && bContainsName) return 1;

      // Priority 3: Job number matches
      const aNumMatch = a.bradanna_job_number?.toLowerCase().includes(search);
      const bNumMatch = b.bradanna_job_number?.toLowerCase().includes(search);
      if (aNumMatch && !bNumMatch) return -1;
      if (!aNumMatch && bNumMatch) return 1;

      // Finally sort by job number descending (newest first)
      const aNum = parseInt(a.bradanna_job_number?.split('-')[1] || '0', 10);
      const bNum = parseInt(b.bradanna_job_number?.split('-')[1] || '0', 10);
      return bNum - aNum;
    });

  // Phone number formatter (reuse from Employee Management)
  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 10) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (field: 'superintendent_phone' | 'project_manager_phone', value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleContractorPhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setContractorFormData({ ...contractorFormData, phone: formatted });
  };

  // Geocode address using Mapbox API
  const geocodeAddress = async () => {
    if (!formData.address || !formData.city || !formData.state) {
      setError('Please enter address, city, and state first');
      return;
    }

    setGeocoding(true);
    setError(null);

    try {
      const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip_code}`;
      const encodedAddress = encodeURIComponent(fullAddress);
      const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiY2xpZmZpc29uIiwiYSI6ImNtNHZqZ3p1cTA3MmcycHE2bDdtdmlwdmYifQ.6kIxcCZXH6I9gHe41FhkMw';

      console.log('Geocoding address:', fullAddress);
      console.log('Mapbox token available:', mapboxToken ? 'Yes' : 'No');

      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&country=US&limit=1`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Geocoding failed');
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;

        // Extract address components from context
        let city = formData.city;
        let state = formData.state;
        let zip = formData.zip_code;

        // Parse context array for city, state, zip
        if (feature.context) {
          feature.context.forEach((item: any) => {
            if (item.id.startsWith('postcode.')) {
              zip = item.text;
            } else if (item.id.startsWith('place.')) {
              city = item.text;
            } else if (item.id.startsWith('region.')) {
              state = item.short_code?.replace('US-', '') || item.text;
            }
          });
        }

        setFormData({
          ...formData,
          city,
          state,
          zip_code: zip,
          lat,
          lng
        });
        setError(null);
      } else {
        setError('Address not found. Please check the address and try again.');
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('Failed to geocode address. Please use map picker instead.');
    } finally {
      setGeocoding(false);
    }
  };

  // Handle map picker
  const handleOpenMapPicker = () => {
    // Allow opening map picker without address - user can pick location directly on map
    setShowMapPicker(true);
  };

  const handleMapLocationSelect = (lat: number, lng: number, radius: number) => {
    setFormData({
      ...formData,
      lat,
      lng,
      // Picking a radius on the map is an explicit conversion to a circle fence.
      geofence_type: 'circle',
      geofence_data: { radius }
    });
    setShowMapPicker(false);
  };

  // Handle add job
  const handleAddJob = () => {
    setEditingJob(null);
    setFormData({
      job_number: '',
      bradanna_job_number: '',
      job_name: '',
      address: '',
      city: '',
      state: 'FL',
      zip_code: '',
      general_contractor: '',
      superintendent_name: '',
      superintendent_phone: '',
      project_manager_name: '',
      project_manager_phone: '',
      bradanna_project_manager: '',
      geofence_type: 'circle',
      geofence_data: { radius: 200 },
      is_asphalt_project: false
    });
    setShowAddModal(true);
  };

  // Handle edit job
  const handleEditJob = (job: Job) => {
    console.log('Editing job:', job);
    console.log('Job coordinates:', { lat: job.lat, lng: job.lng });
    console.log('Job geofence_data:', job.geofence_data);
    console.log('Job contractor_id:', job.contractor_id);

    setEditingJob(job);
    setFormData({
      job_number: job.job_number || '',
      bradanna_job_number: job.bradanna_job_number || '',
      job_name: job.job_name || '',
      address: job.address || '',
      city: job.city || '',
      state: job.state || 'FL',
      zip_code: job.zip_code || '',
      general_contractor: job.general_contractor || '',
      contractor_id: job.contractor_id,
      superintendent_name: job.superintendent_name || '',
      superintendent_phone: job.superintendent_phone || '',
      project_manager_name: job.project_manager_name || '',
      project_manager_phone: job.project_manager_phone || '',
      bradanna_project_manager: job.bradanna_project_manager || '',
      lat: job.lat,
      lng: job.lng,
      // Preserve the job's real geofence — coercing every edit to a 200m circle
      // silently destroyed polygon fences (Alico Crossing, 2026-07-24).
      geofence_type: job.geofence_type || 'circle',
      geofence_data: job.geofence_data || { radius: 200 },
      is_asphalt_project: (job as any).is_asphalt_project || false
    });
    setShowAddModal(true);
  };

  // Save job (add or edit)
  const handleSaveJob = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      const missingFields: string[] = [];
      if (!formData.job_name) missingFields.push('Job Name');
      if (!formData.general_contractor) missingFields.push('General Contractor');
      if (!formData.city) missingFields.push('City');

      if (missingFields.length > 0) {
        showErrorPopup(
          'Missing Required Fields',
          'Please fill in all required fields to continue.',
          missingFields.map(f => `• ${f} is required`)
        );
        setLoading(false);
        return;
      }

      // Spell check the job name on new jobs (AI-backed, fails open)
      let jobNameToUse = formData.job_name;
      if (!editingJob) {
        try {
          const spellRes = await api.post(API_CONFIG.ENDPOINTS.SPELLCHECK_JOB_NAME, { job_name: formData.job_name });
          const suggested = spellRes.data?.data?.suggested;
          if (suggested && suggested !== formData.job_name.trim()) {
            const useSuggestion = confirm(
              `Possible spelling issue in the job name.\n\nTyped:     ${formData.job_name}\nSuggested: ${suggested}\n\nOK = use suggested spelling\nCancel = keep as typed`
            );
            if (useSuggestion) {
              jobNameToUse = suggested;
              setFormData(prev => ({ ...prev, job_name: suggested }));
            }
          }
        } catch (spellError) {
          console.warn('Job name spellcheck unavailable, continuing:', spellError);
        }
      }

      // Auto-geocode if coordinates are missing and address is provided
      let finalFormData = { ...formData, job_name: jobNameToUse };
      if (!formData.lat || !formData.lng) {
        // Only try to geocode if there's an address
        if (formData.address && formData.address.trim()) {
          console.log('Coordinates missing - auto-geocoding address...');
          try {
            const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip_code}`;
            const encodedAddress = encodeURIComponent(fullAddress);
            const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiYmxhY2s5N2d0IiwiYSI6ImNtZnZjbXh2MDA2YXUybm9lcmF2ajQyOXQifQ.2G4lVEYxGsr-d3N2wCM4hw';

            const response = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&country=US&limit=1`
            );

            if (response.ok) {
              const data = await response.json();
              if (data.features && data.features.length > 0) {
                const [lng, lat] = data.features[0].center;
                console.log('Auto-geocoded to:', { lat, lng });
                finalFormData = {
                  ...formData,
                  job_name: jobNameToUse,
                  lat,
                  lng
                };
                // Update form data state so user sees the coordinates
                setFormData(finalFormData);
              } else {
                showErrorPopup(
                  'Address Not Found',
                  'Could not find coordinates for this address.',
                  ['• Try using the "Get Coordinates" button', '• Or use "Select Geofence" to pick location on map']
                );
                setLoading(false);
                return;
              }
            } else {
              showErrorPopup(
                'Geocoding Failed',
                'Failed to get coordinates from the address.',
                ['• Check that the address is correct', '• Try using the "Select Geofence" button instead']
              );
              setLoading(false);
              return;
            }
          } catch (geocodeError) {
            console.error('Auto-geocoding failed:', geocodeError);
            showErrorPopup(
              'Geocoding Error',
              'An error occurred while getting coordinates.',
              ['• Please use the "Select Geofence" button to set location manually']
            );
            setLoading(false);
            return;
          }
        } else {
          // No address, must have coordinates from map picker
          showErrorPopup(
            'Location Required',
            'A job location is required for geofencing.',
            ['• Use "Select Geofence" button to pick location on the map', '• Or enter an address and click "Get Coordinates"']
          );
          setLoading(false);
          return;
        }
      }

      if (editingJob) {
        // Update existing job
        await api.put(`${API_CONFIG.ENDPOINTS.JOBS}/${editingJob.job_id}`, finalFormData);
        setShowAddModal(false);
        loadJobs();
      } else {
        // Create new job
        const response = await api.post(API_CONFIG.ENDPOINTS.JOBS, finalFormData);

        // Update formData with the generated job number from backend
        if (response.data.success && response.data.data.bradanna_job_number) {
          setFormData(prev => ({
            ...prev,
            bradanna_job_number: response.data.data.bradanna_job_number
          }));
          // Show success message with generated job number
          alert(`Job created successfully!\n\nGenerated Job Number: ${response.data.data.bradanna_job_number}`);
        }

        setShowAddModal(false);
        loadJobs();
      }
    } catch (err: any) {
      console.error('Failed to save job:', err);
      const errorMessage = err.response?.data?.error || 'Failed to save job';
      showErrorPopup(
        editingJob ? 'Failed to Update Job' : 'Failed to Create Job',
        errorMessage,
        ['• Please check your input and try again', '• If the problem persists, contact support']
      );
    } finally {
      setLoading(false);
    }
  };

  // Archive job
  const handleArchiveJob = async (job: Job) => {
    if (!confirm(`Are you sure you want to archive "${job.job_name}"?`)) return;

    try {
      setLoading(true);
      await api.post(`${API_CONFIG.ENDPOINTS.JOBS}/${job.job_id}/archive`);
      loadJobs();
    } catch (err: any) {
      console.error('Failed to archive job:', err);
      setError(err.response?.data?.error || 'Failed to archive job');
    } finally {
      setLoading(false);
    }
  };

  // Reactivate job
  const handleReactivateJob = async (job: Job) => {
    if (!confirm(`Are you sure you want to reactivate "${job.job_name}"?`)) return;

    try {
      setLoading(true);
      await api.post(`${API_CONFIG.ENDPOINTS.JOBS}/${job.job_id}/reactivate`);
      loadJobs();
    } catch (err: any) {
      console.error('Failed to reactivate job:', err);
      setError(err.response?.data?.error || 'Failed to reactivate job');
    } finally {
      setLoading(false);
    }
  };

  // Delete job (normal delete - only works if no related data)
  const handleDeleteJob = async (job: Job) => {
    if (!confirm(`Are you sure you want to permanently delete "${job.job_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`${API_CONFIG.ENDPOINTS.JOBS}/${job.job_id}`);
      loadJobs();
      setShowAddModal(false);
    } catch (err: any) {
      console.error('Failed to delete job:', err);
      const errorData = err.response?.data;
      if (errorData && errorData.data && (errorData.data.time_entries_count > 0 || errorData.data.truck_tickets_count > 0)) {
        const { time_entries_count, truck_tickets_count, job_age_years } = errorData.data;
        setError(`Cannot delete job with existing data:\n• ${time_entries_count} time entries\n• ${truck_tickets_count} truck tickets\n\nJob age: ${job_age_years} years\n\nUse Force Delete to override (recommended for jobs 2+ years old).`);
      } else {
        setError(err.response?.data?.error || 'Failed to delete job');
      }
    } finally {
      setLoading(false);
    }
  };

  // Force delete job (deletes all related data)
  const handleForceDeleteJob = async (job: Job) => {
    const jobAge = Math.floor((new Date().getTime() - new Date(job.created_at).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    const ageWarning = jobAge < 2 ? `\n\n⚠️ WARNING: This job is only ${jobAge} years old (less than 2 years). Consider archiving instead.` : '';

    if (!confirm(`🚨 FORCE DELETE: Are you sure you want to permanently delete "${job.job_name}" and ALL related data?\n\nThis will delete:\n• All time clock entries\n• All truck tickets\n• All other job data${ageWarning}\n\nThis action CANNOT be undone!`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete(`${API_CONFIG.ENDPOINTS.JOBS}/${job.job_id}/force`);

      if (response.data.success) {
        const deletedData = response.data.data.deleted_data;
        alert(`✅ Job force deleted successfully!\n\nDeleted:\n• ${deletedData.time_entries_count} time entries\n• ${deletedData.truck_tickets_count} truck tickets\n• Job age: ${deletedData.job_age_years} years`);
      }

      loadJobs();
      setShowAddModal(false);
    } catch (err: any) {
      console.error('Failed to force delete job:', err);
      setError(err.response?.data?.error || 'Failed to force delete job');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '14px'
    },
    controls: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '16px',
      marginBottom: '24px'
    },
    searchSection: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px'
    },
    searchInput: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px',
      marginBottom: '12px'
    },
    viewToggle: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px'
    },
    toggleButton: {
      padding: '10px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      backgroundColor: 'white',
      color: '#6b7280',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s'
    },
    toggleButtonActive: {
      backgroundColor: '#3b82f6',
      color: 'white',
      borderColor: '#3b82f6'
    },
    addButton: {
      padding: '12px 16px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500'
    },
    jobList: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    jobCard: {
      padding: '16px',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px'
    },
    archivedCard: {
      backgroundColor: '#f9fafb',
      opacity: 0.7
    },
    jobHeader: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px'
    },
    jobInfo: {
      flex: 1
    },
    jobTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px'
    },
    jobNumber: {
      fontSize: '14px',
      color: '#3b82f6',
      fontWeight: '500',
      marginBottom: '8px'
    },
    jobDetails: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px'
    },
    detailRow: {
      fontSize: '14px',
      color: '#4b5563',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    statusTag: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase' as const
    },
    activeStatus: {
      backgroundColor: '#dcfce7',
      color: '#15803d'
    },
    archivedStatus: {
      backgroundColor: '#fef3c7',
      color: '#92400e'
    },
    jobActions: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
      minWidth: '140px'
    },
    actionButton: {
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    editButton: {
      color: '#3b82f6',
      borderColor: '#3b82f6'
    },
    archiveButton: {
      color: '#f59e0b',
      borderColor: '#f59e0b'
    },
    reactivateButton: {
      color: '#10b981',
      borderColor: '#10b981'
    },
    deleteButton: {
      color: '#dc2626',
      borderColor: '#dc2626'
    },
    // Modal styles (reuse from Employee Management)
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '100%',
      maxWidth: '800px',
      maxHeight: '90vh',
      overflowY: 'auto' as const
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid #e5e7eb'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937'
    },
    closeButton: {
      padding: '4px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: '#6b7280'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      marginBottom: '16px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    formGroupFull: {
      gridColumn: '1 / -1'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '16px',
      backgroundColor: 'white'
    },
    geocodeButton: {
      padding: '8px 12px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginTop: '8px'
    },
    modalActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '24px',
      paddingTop: '12px',
      borderTop: '1px solid #e5e7eb'
    },
    saveButton: {
      padding: '10px 20px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    cancelButton: {
      padding: '10px 20px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px'
    },
    loading: {
      textAlign: 'center' as const,
      padding: '40px',
      color: '#6b7280'
    },
    error: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '16px'
    },
    noData: {
      textAlign: 'center' as const,
      padding: '40px',
      color: '#6b7280',
      fontStyle: 'italic'
    },
    editButtonPrimary: {
      padding: '12px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      marginTop: '8px',
      marginBottom: '12px',
      width: 'fit-content'
    },
    dangerZone: {
      marginTop: '24px',
      padding: '16px',
      border: '1px solid #fecaca',
      borderRadius: '6px',
      backgroundColor: '#fef2f2'
    },
    dangerTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#dc2626',
      marginBottom: '12px',
      margin: 0
    },
    dangerActions: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px'
    },
    archiveButtonModal: {
      padding: '10px 16px',
      backgroundColor: '#f59e0b',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'center'
    },
    reactivateButtonModal: {
      padding: '10px 16px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'center'
    },
    deleteButtonModal: {
      padding: '10px 16px',
      backgroundColor: '#dc2626',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'center'
    },
    geofenceControls: {
      backgroundColor: '#f8fafc',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '16px'
    },
    geofenceRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px'
    },
    geofenceLabel: {
      fontSize: '14px',
      fontWeight: '500',
      minWidth: '60px',
      color: '#374151'
    },
    rangeInput: {
      flex: 1,
      height: '6px',
      background: '#e5e7eb',
      borderRadius: '3px',
      outline: 'none',
      appearance: 'none' as const
    },
    radiusDisplay: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#3b82f6',
      minWidth: '50px',
      textAlign: 'right' as const
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Job Management</h1>
        <p style={styles.subtitle}>Manage construction jobs, locations, and geofencing</p>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      <div style={styles.controls}>
        <div style={styles.searchSection}>
          <input
            type="text"
            placeholder="Search jobs by name, number, or contractor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />

          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.toggleButton,
                ...(viewMode === 'active' ? styles.toggleButtonActive : {})
              }}
              onClick={() => setViewMode('active')}
            >
              <Briefcase size={16} />
              Active Jobs ({jobs.filter(j => j.is_active).length})
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(viewMode === 'archived' ? styles.toggleButtonActive : {})
              }}
              onClick={() => setViewMode('archived')}
            >
              <Archive size={16} />
              Archived ({jobs.filter(j => !j.is_active).length})
            </button>
            <button
              style={{
                ...styles.toggleButton,
                ...(viewMode === 'all' ? styles.toggleButtonActive : {})
              }}
              onClick={() => setViewMode('all')}
            >
              <Briefcase size={16} />
              All Jobs ({jobs.length})
            </button>
          </div>

          {auth.user.role === 'admin' && (
            <button style={styles.addButton} onClick={handleAddJob}>
              <Plus size={16} />
              Add New Job
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>
          <p>Loading jobs...</p>
        </div>
      ) : (
        <div style={styles.jobList}>
          {filteredJobs.length === 0 ? (
            <div style={styles.noData}>
              No jobs found
            </div>
          ) : (
            filteredJobs.map(job => (
              <div
                key={job.job_id}
                style={{
                  ...styles.jobCard,
                  ...(job.is_active ? {} : styles.archivedCard)
                }}
              >
                <div style={styles.jobHeader}>
                  <div style={styles.jobInfo}>
                    <div style={styles.jobTitle}>
                      {job.job_name}
                      <span style={{
                        ...styles.statusTag,
                        ...(job.is_active ? styles.activeStatus : styles.archivedStatus),
                        marginLeft: '12px'
                      }}>
                        {job.is_active ? 'Active' : 'Archived'}
                      </span>
                    </div>

                    {/* Edit button below job name for easy access */}
                    <button
                      style={styles.editButtonPrimary}
                      onClick={() => handleEditJob(job)}
                    >
                      <Edit size={16} />
                      Edit Job Details
                    </button>

                    {job.bradanna_job_number && (
                      <div style={styles.jobNumber}>
                        {job.bradanna_job_number}
                      </div>
                    )}
                    <div style={styles.jobDetails}>
                      <div style={styles.detailRow}>
                        <MapPin size={14} />
                        <strong>Location:</strong> {job.address}, {job.city}, {job.state} {job.zip_code}
                      </div>
                      <div style={styles.detailRow}>
                        <Building size={14} />
                        <strong>Contractor:</strong> {job.general_contractor}
                      </div>
                      <div style={styles.detailRow}>
                        <User size={14} />
                        <strong>Superintendent:</strong> {job.superintendent_name}
                        {job.superintendent_phone && (
                          <span style={{ color: '#3b82f6' }}>({job.superintendent_phone})</span>
                        )}
                      </div>
                      <div style={styles.detailRow}>
                        <User size={14} />
                        <strong>PM:</strong> {job.project_manager_name}
                        {job.project_manager_phone && (
                          <span style={{ color: '#3b82f6' }}>({job.project_manager_phone})</span>
                        )}
                      </div>
                      <div style={styles.detailRow}>
                        <Calendar size={14} />
                        <strong>Created:</strong> {new Date(job.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Job Modal */}
      {showAddModal && (
        <div
          style={styles.modal}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setTimeout(() => setShowAddModal(false), 10);
            }
          }}
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingJob ? 'Edit Job' : 'Add New Job'}
              </h2>
              <button style={styles.closeButton} onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>

            {error && (
              <div style={styles.error}>
                {error}
                <button
                  onClick={() => setError(null)}
                  style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Job Name - Full Width */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Job Name *</label>
              <input
                type="text"
                style={styles.input}
                value={formData.job_name}
                onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                placeholder="Main Street Construction"
              />
            </div>

            {/* General Contractor - Full Width */}
            <div style={styles.formGroup}>
              <label style={styles.label}>General Contractor *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.general_contractor}
                  onChange={(e) => {
                    setFormData({ ...formData, general_contractor: e.target.value, contractor_id: undefined });
                    setShowContractorDropdown(true);
                  }}
                  onFocus={() => setShowContractorDropdown(true)}
                  onBlur={() => setTimeout(() => setShowContractorDropdown(false), 200)}
                  placeholder="Start typing contractor name..."
                />
                {showContractorDropdown && filteredContractors.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    {filteredContractors.map((contractor) => (
                      <div
                        key={contractor.contractor_id}
                        style={{
                          padding: '12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          fontSize: '14px',
                          color: '#374151',
                          backgroundColor: 'white'
                        }}
                        onMouseDown={() => {
                          setFormData({
                            ...formData,
                            general_contractor: contractor.contractor_name,
                            contractor_id: contractor.contractor_id
                          });
                          setShowContractorDropdown(false);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        {contractor.contractor_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleAddContractor}
                style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Plus size={16} />
                Add Contractor
              </button>
            </div>

            {/* Asphalt Project Checkbox */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: editingJob ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_asphalt_project}
                  onChange={(e) => setFormData({ ...formData, is_asphalt_project: e.target.checked })}
                  disabled={!!editingJob}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Is this an asphalt project?
                  {editingJob && <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>(Cannot change after creation)</span>}
                </span>
              </label>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '26px' }}>
                {new Date().getFullYear() === 2025
                  ? formData.is_asphalt_project
                    ? '2025 Asphalt jobs: 25-200 to 25-499 range'
                    : '2025 Site jobs: 25-100 to 25-199 range'
                  : formData.is_asphalt_project
                    ? 'Asphalt jobs: 500-599 range (e.g., 26-500, 26-501...)'
                    : 'Site jobs: 100-499 range (e.g., 26-100, 26-101...)'}
              </div>
              {!editingJob && previewJobNumber && (
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#059669',
                  marginTop: '8px',
                  marginLeft: '26px',
                  padding: '8px 12px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '6px',
                  display: 'inline-block'
                }}>
                  Next Job Number: {previewJobNumber}
                </div>
              )}
            </div>

            {/* Show job number only when editing existing job */}
            {editingJob && formData.bradanna_job_number && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <label style={styles.label}>Bradanna Job Number</label>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: '4px' }}>
                  {formData.bradanna_job_number}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Job number is auto-generated and cannot be changed
                </div>
              </div>
            )}

            {/* Address Section */}
            <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
              <label style={styles.label}>Address</label>
              <input
                type="text"
                style={styles.input}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street"
              />
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>City *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Naples"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>State</label>
                <CustomSelect
                  value={formData.state}
                  onChange={(value) => setFormData({ ...formData, state: value })}
                  placeholder="Select State"
                  options={[
                    { value: 'FL', label: 'Florida' },
                    { value: 'GA', label: 'Georgia' },
                    { value: 'AL', label: 'Alabama' }
                  ]}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>ZIP Code</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="34116"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Location Coordinates</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button
                    style={{
                      ...styles.geocodeButton,
                      flex: 1,
                      opacity: geocoding ? 0.6 : 1
                    }}
                    onClick={geocodeAddress}
                    disabled={geocoding}
                    type="button"
                  >
                    <MapPin size={14} />
                    {geocoding ? 'Getting...' : 'Get Coordinates'}
                  </button>
                  <button
                    style={{
                      ...styles.geocodeButton,
                      flex: 1,
                      backgroundColor: '#10b981'
                    }}
                    onClick={handleOpenMapPicker}
                    type="button"
                  >
                    <MapIcon size={14} />
                    Select Geofence
                  </button>
                </div>
                {formData.lat && formData.lng && (
                  <div style={{ fontSize: '12px', color: '#22c55e', marginTop: '4px', fontWeight: '600' }}>
                    ✓ Coordinates: {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                  </div>
                )}
                {!formData.lat && !formData.lng && (
                  <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                    ⚠ Coordinates required for geofencing
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Superintendent Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.superintendent_name}
                  onChange={(e) => setFormData({ ...formData, superintendent_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Superintendent Phone</label>
                <input
                  type="tel"
                  style={styles.input}
                  value={formData.superintendent_phone}
                  onChange={(e) => handlePhoneChange('superintendent_phone', e.target.value)}
                  placeholder="Enter phone number (auto-formats)"
                  maxLength={12}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Project Manager Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.project_manager_name}
                  onChange={(e) => setFormData({ ...formData, project_manager_name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Project Manager Phone</label>
                <input
                  type="tel"
                  style={styles.input}
                  value={formData.project_manager_phone}
                  onChange={(e) => handlePhoneChange('project_manager_phone', e.target.value)}
                  placeholder="Enter phone number (auto-formats)"
                  maxLength={12}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Bradanna Project Manager</label>
              <input
                type="text"
                style={styles.input}
                value={formData.bradanna_project_manager}
                onChange={(e) => setFormData({ ...formData, bradanna_project_manager: e.target.value })}
                placeholder="Bob Johnson"
              />
            </div>

            {/* Geofence Configuration */}
            {formData.lat && formData.lng && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Geofence Radius (meters)</label>
                <div style={styles.geofenceControls}>
                  <div style={styles.geofenceRow}>
                    <input
                      type="range"
                      min="50"
                      max="1000"
                      step="10"
                      value={formData.geofence_data.radius ?? 200}
                      onChange={(e) => setFormData({
                        ...formData,
                        // Moving the slider explicitly converts to a circle fence.
                        geofence_type: 'circle',
                        geofence_data: { radius: parseInt(e.target.value) }
                      })}
                      style={styles.rangeInput}
                    />
                    <span style={styles.radiusDisplay}>
                      {formData.geofence_data.radius ?? 200}m ({Math.round((formData.geofence_data.radius ?? 200) * 3.28084)}ft)
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                    {formData.geofence_type === 'polygon'
                      ? 'This job has a polygon geofence — moving the slider replaces it with a circle'
                      : 'Employees can only clock in within this radius of the job location'}
                  </div>
                </div>
              </div>
            )}

            {/* Archive and Delete buttons (only when editing) */}
            {editingJob && (
              <div style={styles.dangerZone}>
                <h3 style={styles.dangerTitle}>Job Actions</h3>
                <div style={styles.dangerActions}>
                  {editingJob.is_active ? (
                    <button
                      style={styles.archiveButtonModal}
                      onClick={() => {
                        setShowAddModal(false);
                        handleArchiveJob(editingJob);
                      }}
                    >
                      <Archive size={16} />
                      Archive Job
                    </button>
                  ) : (
                    auth.user.role === 'admin' && (
                      <button
                        style={styles.reactivateButtonModal}
                        onClick={() => {
                          setShowAddModal(false);
                          handleReactivateJob(editingJob);
                        }}
                      >
                        <RotateCcw size={16} />
                        Reactivate Job
                      </button>
                    )
                  )}

                  {auth.user.role === 'admin' && auth.user.email === 'cliff@bradanna.com' && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        style={styles.deleteButtonModal}
                        onClick={() => {
                          handleDeleteJob(editingJob);
                        }}
                      >
                        <Trash2 size={16} />
                        Delete Job
                      </button>
                      <button
                        style={{
                          ...styles.deleteButtonModal,
                          backgroundColor: '#dc2626',
                          borderColor: '#dc2626',
                          color: 'white'
                        }}
                        onClick={() => {
                          handleForceDeleteJob(editingJob);
                        }}
                        title="Force delete with all related data (time entries & truck tickets)"
                      >
                        <Trash2 size={16} />
                        Force Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button style={styles.saveButton} onClick={handleSaveJob} disabled={loading}>
                <Save size={16} />
                {editingJob ? 'Update Job' : 'Create Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contractor Modal */}
      {showAddContractorModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add New Contractor</h2>
              <button
                style={styles.closeButton}
                onClick={() => setShowAddContractorModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            {error && (
              <div style={{
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <div style={styles.formGrid}>
              <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
                <label style={styles.label}>Contractor Name *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={contractorFormData.contractor_name}
                  onChange={(e) => setContractorFormData({ ...contractorFormData, contractor_name: e.target.value })}
                  placeholder="ABC Construction Company"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Contact Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={contractorFormData.contact_name}
                  onChange={(e) => setContractorFormData({ ...contractorFormData, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  style={styles.input}
                  value={contractorFormData.phone}
                  onChange={(e) => handleContractorPhoneChange(e.target.value)}
                  placeholder="239-555-0100"
                />
              </div>

              <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  style={styles.input}
                  value={contractorFormData.email}
                  onChange={(e) => setContractorFormData({ ...contractorFormData, email: e.target.value })}
                  placeholder="contact@abcconstruction.com"
                />
              </div>

              <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
                <label style={styles.label}>Address</label>
                <input
                  type="text"
                  style={styles.input}
                  value={contractorFormData.address}
                  onChange={(e) => setContractorFormData({ ...contractorFormData, address: e.target.value })}
                  placeholder="123 Main St, Fort Myers, FL 33901"
                />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                style={styles.cancelButton}
                onClick={() => {
                  setShowAddContractorModal(false);
                  setError(null);
                }}
              >
                Cancel
              </button>
              <button
                style={styles.saveButton}
                onClick={handleSaveContractor}
                disabled={loading}
              >
                <Save size={16} />
                Add Contractor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Map Picker */}
      <FullScreenMapPicker
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        onLocationSelect={handleMapLocationSelect}
        initialLat={formData.lat || 26.1420}
        initialLng={formData.lng || -81.7948}
        address={formData.address ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip_code}` : ''}
        geofenceType="circle"
        geofenceRadius={formData.geofence_data.radius || 200}
      />

      {/* Error Popup Modal */}
      {showErrorModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '0',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden'
          }}>
            {/* Error Header */}
            <div style={{
              backgroundColor: '#dc2626',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertCircle size={24} color="white" />
              <h3 style={{
                margin: 0,
                color: 'white',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {errorTitle}
              </h3>
            </div>

            {/* Error Content */}
            <div style={{ padding: '20px' }}>
              <p style={{
                margin: '0 0 16px 0',
                color: '#374151',
                fontSize: '15px',
                lineHeight: '1.5'
              }}>
                {error}
              </p>

              {errorDetails.length > 0 && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px 16px'
                }}>
                  {errorDetails.map((detail, index) => (
                    <p key={index} style={{
                      margin: index === errorDetails.length - 1 ? 0 : '0 0 8px 0',
                      color: '#dc2626',
                      fontSize: '14px'
                    }}>
                      {detail}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Error Actions */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setError(null);
                  setErrorDetails([]);
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};